import React, { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { objectivesApi, exercisesApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { FileText, Plus } from 'lucide-react';
import ObjectiveQuickViewModal from '../../components/ems/ObjectiveQuickViewModal';

export default function ObjectivesPage() {
  const { isController } = useAuth();
  const [loading, setLoading] = useState(true);
  const [objectives, setObjectives] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [exerciseFilter, setExerciseFilter] = useState('all');

  const [viewOpen, setViewOpen] = useState(false);
  const [viewObjective, setViewObjective] = useState(null);
  
  // Create objective dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newObjective, setNewObjective] = useState({
    exercise_id: '',
    objective_code: '',
    objective_text: '',
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [objData, exData] = await Promise.all([
        objectivesApi.getAll(exerciseFilter === 'all' ? undefined : exerciseFilter),
        exercisesApi.getAll(),
      ]);
      setObjectives(objData);
      setExercises(exData);
    } catch (e) {
      toast.error('Failed to load objectives');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [exerciseFilter]);

  const openView = (obj) => {
    setViewObjective(obj);
    setViewOpen(true);
  };

  const handleCreateObjective = async (e) => {
    e.preventDefault();
    if (!newObjective.exercise_id || !newObjective.objective_code) {
      toast.error('Please fill required fields');
      return;
    }
    
    try {
      setCreating(true);
      await objectivesApi.create(newObjective);
      toast.success('Objective created');
      setCreateOpen(false);
      setNewObjective({ exercise_id: '', objective_code: '', objective_text: '' });
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to create objective');
    } finally {
      setCreating(false);
    }
  };

  if (!isController()) {
    return (
      <DashboardLayout title="Objectives">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">Not allowed</CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Objectives">
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Select value={exerciseFilter} onValueChange={setExerciseFilter}>
              <SelectTrigger className="w-[260px]">
                <SelectValue placeholder="Filter by exercise" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Exercises</SelectItem>
                {exercises.map((ex) => (
                  <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                    {ex.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <Button onClick={() => setCreateOpen(true)} data-testid="create-objective-btn">
            <Plus className="w-4 h-4 mr-2" />
            Create Objective
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : objectives.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center text-muted-foreground">
              <p>No objectives found</p>
              <Button onClick={() => setCreateOpen(true)} className="mt-4" variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create First Objective
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {objectives.map((obj) => (
              <Card key={obj.objective_id} className="hover:border-primary/30 transition-colors">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">{obj.objective_code}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-sm text-muted-foreground line-clamp-3">{obj.objective_text || '-'}</p>
                  <Button variant="outline" onClick={() => openView(obj)}>
                    <FileText className="w-4 h-4 mr-2" />
                    View
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <ObjectiveQuickViewModal
          open={viewOpen}
          onOpenChange={setViewOpen}
          objective={viewObjective}
          onOpenNotes={() => setViewOpen(false)}
        />

        {/* Create Objective Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-secondary">Create Objective</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateObjective} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="obj-exercise">Exercise *</Label>
                <Select 
                  value={newObjective.exercise_id} 
                  onValueChange={(v) => setNewObjective({...newObjective, exercise_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select exercise" />
                  </SelectTrigger>
                  <SelectContent>
                    {exercises.map((ex) => (
                      <SelectItem key={ex.exercise_id} value={ex.exercise_id}>
                        {ex.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="obj-code">Objective Code *</Label>
                <Input
                  id="obj-code"
                  value={newObjective.objective_code}
                  onChange={(e) => setNewObjective({...newObjective, objective_code: e.target.value})}
                  placeholder="e.g., OBJ-001"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="obj-text">Objective Text</Label>
                <Textarea
                  id="obj-text"
                  value={newObjective.objective_text}
                  onChange={(e) => setNewObjective({...newObjective, objective_text: e.target.value})}
                  placeholder="Describe the objective..."
                  rows={3}
                />
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={creating}>
                  {creating ? 'Creating...' : 'Create Objective'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
